import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http'
import { APP_INITIALIZER, ErrorHandler, NgModule, SecurityContext } from '@angular/core'
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app'
import { getAuth, provideAuth } from '@angular/fire/auth'
import { AngularFireModule } from '@angular/fire/compat'
import { AngularFireAuthModule, TENANT_ID } from '@angular/fire/compat/auth'
import { AngularFirestoreModule } from '@angular/fire/compat/firestore'
import { initializeFirestore, provideFirestore } from '@angular/fire/firestore'
import { getStorage, provideStorage } from '@angular/fire/storage'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { GoogleMapsModule } from '@angular/google-maps'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { CloudflareStreamModule } from '@cloudflare/stream-angular'
import { ConsumerTenantBootstrapConfigProvider } from '@consumer/app/config/tenant.config'
import * as fromStore from '@consumer/app/store/index'
import { AccessModule } from '@engineering11/access-web'
import { NgAisModule } from '@engineering11/angular-instantsearch'
import { FilesModule } from '@engineering11/files-web'
import { MapsModule } from '@engineering11/maps-web'
import { AppConfigService, TenantBootstrapModule } from '@engineering11/tenant-bootstrap-web'
import { E11BackdropModule } from '@engineering11/ui-lib/e11-backdrop'
import { E11NotificationsModule } from '@engineering11/ui-lib/e11-notifications'
import { E11SideOverModule } from '@engineering11/ui-lib/e11-side-over'
import { E11ToastModule } from '@engineering11/ui-lib/e11-toast'
import { E11ErrorModule, GlobalErrorHandler } from '@engineering11/web-api-error'
import { E11AlertNotificationService } from '@engineering11/web-ui-helpers'
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { RECAPTCHA_SETTINGS, RecaptchaModule } from 'ng-recaptcha'
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input'
import { LoggerModule, NGXLogger, NgxLoggerLevel } from 'ngx-logger'
import { MarkdownModule } from 'ngx-markdown'
import { WebpackTranslateLoader, errorHandlerOverrides } from 'shared-lib'
import { environment } from '../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { AboutComponent } from './components/about/about.component'
import { ToastComponent } from './components/toast/toast.component'
import { AccessConfigProvider } from './config/access.config'
import { ISDFilesConfigProvider } from './config/files.config'
import { componentStateReducer } from './config/link-global.state'
import { ServerErrorInterceptor } from './error-server.interceptor'
import { DenyGuard } from './guards/deny.guard'
import { httpInterceptorProviders } from './http-interceptors'
import { FileNotFoundModule } from './modules/_404/404.module'
import { UiComponentLibraryModule } from './modules/_component-library/component-library.module'
import { SharedModule } from './modules/_shared/shared.module'
import { AuthenticationModule } from './modules/authentication/authentication.module'
import { CustomerModule } from './modules/customer/customer.module'
import { HomeModule } from './modules/home/home.module'
import { NotificationModule } from './modules/notification/notification.module'
import { RegistrationModule } from './modules/registration/registration.module'
import { rollbarFactory } from './rollbar'
import { ConfigService } from './services/config.service'

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json')
}
export const configFactory = (configService: ConfigService) => {
  return () => configService.loadConfig()
}

@NgModule({
  declarations: [AppComponent, AboutComponent, ToastComponent],
  imports: [
    NgAisModule.forRoot(),
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    MarkdownModule.forRoot({
      sanitize: SecurityContext.NONE,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: WebpackTranslateLoader,
        // useFactory: HttpLoaderFactory,
        // deps: [HttpClient],
      },
    }),
    CustomerModule,
    SharedModule,
    UiComponentLibraryModule,
    CloudflareStreamModule,
    RecaptchaModule,
    AuthenticationModule,
    RegistrationModule,
    HomeModule,
    NotificationModule,
    FileNotFoundModule,
    // UiBackdropModule.forRoot(),
    E11BackdropModule.forRoot(),
    E11ToastModule.forRoot(),
    E11NotificationsModule.forRoot(),
    E11SideOverModule,

    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireAuthModule,
    AngularFirestoreModule,
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideFirestore(() => initializeFirestore(getApp(), { ignoreUndefinedProperties: true })),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    HttpClientModule,

    LoggerModule.forRoot({ level: !environment.production ? NgxLoggerLevel.LOG : NgxLoggerLevel.OFF, serverLogLevel: NgxLoggerLevel.OFF }),
    StoreModule.forRoot({ ...fromStore.reducers, componentStore: componentStateReducer }),
    EffectsModule.forRoot(fromStore.effects),
    !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 50, connectInZone: true }) : [],
    BrowserAnimationsModule,
    NgxIntlTelInputModule,
    FilesModule.forRoot({ configProvider: ISDFilesConfigProvider }),
    GoogleMapsModule,
    MapsModule.forRoot({ mapsApiKey: environment.firebaseConfig.apiKey }),
    E11ErrorModule.forRoot({
      errorTrackerFactory: rollbarFactory,
      logger: NGXLogger,
      errorHandlerMap: errorHandlerOverrides,
      alertService: E11AlertNotificationService,
    }),
    TenantBootstrapModule.forRoot({ configProvider: ConsumerTenantBootstrapConfigProvider }),
    AccessModule.forRoot({ configProvider: AccessConfigProvider }),
    // BillingModule,
  ],
  providers: [
    DenyGuard,
    {
      provide: APP_INITIALIZER,
      useFactory: configFactory,
      deps: [ConfigService],
      multi: true,
    },
    httpInterceptorProviders,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: TENANT_ID, useValue: environment.tenantId },
    {
      provide: RECAPTCHA_SETTINGS,
      useFactory: (appConfig: AppConfigService) => {
        return { siteKey: appConfig.config.auth.reCaptchaSiteKey }
      },
      deps: [AppConfigService],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
